import * as React from 'react'
import { Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { IntuiCalendar, type IntuiCalendarProps } from './IntuiCalendar'

/**
 * Figma (node 21203:54477 — Calendar / Date and Time Picker):
 *
 * Single card container: w-[220px], bg-card, border border-border, rounded-xl
 * Calendar: bare, default size (28px cells), pt-2 px-2
 * Time section: border-t, p-3, space-y-3
 * Labels: text-sm font-medium
 * Time inputs: h-8, rounded-lg, border border-input, pl-8 (for clock icon), text-sm
 */

const timeInputClass = cn(
  'flex h-8 w-full rounded-lg border border-input bg-background pl-8 pr-2.5 py-1',
  'text-sm text-foreground',
  'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50',
)

export interface IntuiCalendarDateTimeProps extends Omit<IntuiCalendarProps, 'mode' | 'bare'> {
  /** Currently selected date */
  selected?: Date
  /** Callback when date changes */
  onSelect?: (date: Date | undefined) => void
  /** Start time value (HH:mm:ss) */
  startTime?: string
  /** End time value (HH:mm:ss) */
  endTime?: string
  /** Callback when start time changes */
  onStartTimeChange?: (time: string) => void
  /** Callback when end time changes */
  onEndTimeChange?: (time: string) => void
  /** Additional className for the outer card */
  wrapperClassName?: string
}

function IntuiCalendarDateTime({
  selected,
  onSelect,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  wrapperClassName,
  className,
  ...calendarProps
}: IntuiCalendarDateTimeProps) {
  const { t } = useTranslation()
  const uniqueId = React.useId()
  const startId = `${uniqueId}-start-time`
  const endId = `${uniqueId}-end-time`

  return (
    <div
      className={cn(
        'bg-card border border-border rounded-xl overflow-hidden',
        wrapperClassName,
      )}
    >
      {/* Calendar area */}
      <div className="pt-2 px-1">
        <IntuiCalendar
          mode="single"
          selected={selected}
          onSelect={onSelect}
          bare
          className={className}
          {...calendarProps}
        />
      </div>

      {/* Time inputs */}
      <div className="border-t border-border p-3 space-y-3">
        <div className="space-y-1.5">
          <label htmlFor={startId} className="text-sm font-medium text-foreground">{t('common:date_picker.start_time', 'Start Time')}</label>
          <div className="relative">
            <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              id={startId}
              type="time"
              step={1}
              value={startTime ?? ''}
              onChange={(e) => onStartTimeChange?.(e.target.value)}
              className={timeInputClass}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label htmlFor={endId} className="text-sm font-medium text-foreground">{t('common:date_picker.end_time', 'End Time')}</label>
          <div className="relative">
            <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              id={endId}
              type="time"
              step={1}
              value={endTime ?? ''}
              onChange={(e) => onEndTimeChange?.(e.target.value)}
              className={timeInputClass}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
IntuiCalendarDateTime.displayName = 'IntuiCalendarDateTime'

export { IntuiCalendarDateTime }
